/**
 * Dropping a team's Shared Docs index when the membership behind it ends.
 *
 * The bundle keeps a project's document and folder index in scope-keyed atoms
 * that outlive the session that filled them, and that is deliberate: a reader
 * who has opened Shared Docs once keeps those documents searchable from
 * Trackers, where no docs session is mounted at all. Disposing a session does
 * not clear them, and nothing else does either.
 *
 * That retention is only defensible while the reader is still a member. Once
 * membership is terminally gone -- the team session comes back
 * `member-mismatch` or `organization-unavailable`, or a live tracker room
 * reports `removed-from-org` -- the titles are a copy of team data this tab may
 * no longer read, and ⌘K would still find them. So the removal event prunes,
 * rather than the atoms giving up their lifetime for everyone.
 *
 * Every project the team lists is pruned, not just the open one: the reader may
 * have visited several in this tab, and only the ones they visited hold
 * anything, so pruning the rest is free.
 */

import { pruneCollabDocsSession } from '@nimbalyst/collab-bundle/docs-ui';
import type { TeamSummary } from '../api';
import { TeamSessionError } from '../auth/teamJwt';
import { getProjectRouteId } from '../projectSelection';
import { webCollabScopeKey } from './WebCollabHost';

/**
 * Whether a team-session failure means the membership is gone, as opposed to
 * merely unreachable.
 *
 * The distinction is the whole safety of pruning on it. `service-unavailable`
 * and `session-expired` are failures of the *path* to the membership -- a dead
 * Stytch, a lapsed token, a flaky network -- and say nothing about whether this
 * reader is still a member. Widening this to "any team-session error" would
 * throw away a current member's document index every time the network hiccuped;
 * `wrong-org` and `malformed-token` are about the token presented, not the
 * membership behind it.
 */
export function isTerminalMembershipFailure(error: unknown): boolean {
  return error instanceof TeamSessionError
    && (error.kind === 'member-mismatch' || error.kind === 'organization-unavailable');
}

export function teamDocsScopeKeys(team: TeamSummary): string[] {
  return team.projects.map((project) => webCollabScopeKey(team.orgId, getProjectRouteId(project)));
}

export function pruneTeamDocsScopes(team: TeamSummary): void {
  for (const scopeKey of teamDocsScopeKeys(team)) pruneCollabDocsSession(scopeKey);
}
